/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Hero 1 PD component — full-bleed image hero with eyebrow, title, sub-copy, dual CTAs, legal text
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

/* v8 ignore start */
@Component('heroOne', {
    name: 'Hero 1',
    description: 'Full-bleed image hero with eyebrow, title, sub-copy, up to two CTAs, and legal text.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class HeroOneMetadata {
    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow / Tagline', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'subCopy', name: 'Sub Copy', type: 'text' })
    subCopy?: string;

    @AttributeDefinition({ id: 'ctaOneText', name: 'Primary CTA Text', type: 'string' })
    ctaOneText?: string;

    @AttributeDefinition({ id: 'ctaOneLink', name: 'Primary CTA Link', type: 'url' })
    ctaOneLink?: string;

    @AttributeDefinition({ id: 'ctaTwoText', name: 'Secondary CTA Text', type: 'string' })
    ctaTwoText?: string;

    @AttributeDefinition({ id: 'ctaTwoLink', name: 'Secondary CTA Link', type: 'url' })
    ctaTwoLink?: string;

    @AttributeDefinition({ id: 'legalText', name: 'Legal Text', type: 'string' })
    legalText?: string;

    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;
}
/* v8 ignore stop */

export default function HeroOne({
    eyebrow,
    title,
    subCopy,
    ctaOneText,
    ctaOneLink,
    ctaTwoText,
    ctaTwoLink,
    legalText,
    image,
    imageAlt,
}: {
    eyebrow?: string;
    title?: string;
    subCopy?: string;
    ctaOneText?: string;
    ctaOneLink?: string;
    ctaTwoText?: string;
    ctaTwoLink?: string;
    legalText?: string;
    image?: Image;
    imageAlt?: string;
}) {
    return (
        <div className="relative w-full min-h-[80vh] lg:min-h-[87.5vh] bg-background overflow-hidden flex items-center">
            {image?.url && (
                <DynamicImage
                    src={image.url}
                    alt={imageAlt ?? ''}
                    widths={['100vw']}
                    // Below the promo-countdown banner in DOM order, so it's below the fold on mobile and
                    // never the LCP. Left at auto (lazy) so it doesn't contend with the real LCP image's
                    // high-priority preload on the mobile connection.
                    priority="auto"
                    className="absolute inset-0 w-full h-full [&_picture]:block [&_picture]:w-full [&_picture]:h-full"
                    imageProps={{ className: 'w-full h-full object-cover' }}
                />
            )}

            {/* RGBARRY-CUSTOMIZATION: stretched link makes the whole hero (image included) clickable to
                the primary CTA destination, while both CTA buttons below remain their own click targets */}
            {ctaOneText && ctaOneLink && <StretchedLink to={ctaOneLink} className="z-[6]" />}

            <div className="relative z-10 px-6 sm:px-10 lg:px-14 max-w-2xl pointer-events-none">
                {eyebrow && (
                    <p className="df-hero-label text-xs tracking-[0.22em] uppercase text-primary-foreground/70 mb-4">
                        {eyebrow}
                    </p>
                )}
                {title && (
                    <h2
                        style={{ fontFamily: 'var(--font-display)' }}
                        className="df-hero-h1 text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-primary-foreground mb-6">
                        {title}
                    </h2>
                )}
                {subCopy && (
                    <p className="df-hero-body text-sm lg:text-base text-primary-foreground/80 leading-relaxed mb-8 max-w-md">
                        {subCopy}
                    </p>
                )}
                {(ctaOneText && ctaOneLink) || (ctaTwoText && ctaTwoLink) ? (
                    <div className="flex flex-wrap gap-3 mb-4 pointer-events-auto">
                        {ctaOneText && ctaOneLink && (
                            <Button
                                asChild
                                className="df-hero-cta df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 bg-background text-foreground hover:bg-background/90">
                                <Link to={ctaOneLink}>{ctaOneText}</Link>
                            </Button>
                        )}
                        {ctaTwoText && ctaTwoLink && (
                            <Button
                                asChild
                                variant="outline"
                                className="df-hero-cta rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-foreground">
                                <Link to={ctaTwoLink}>{ctaTwoText}</Link>
                            </Button>
                        )}
                    </div>
                ) : null}
                {legalText && (
                    <p className={cn('df-hero-cta text-[11px] text-primary-foreground/50 leading-relaxed')}>
                        {legalText}
                    </p>
                )}
            </div>
        </div>
    );
}
